import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

// Use relative URLs for API calls
const API_URL = '';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      // Always show success message for security (don't reveal if email exists)
      setSuccess(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8 bg-white/5 border-gray-800 backdrop-blur">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Check Your Email</h2>
            <p className="text-gray-400 mb-6">
              If an account exists with <strong className="text-white">{email}</strong>, we&apos;ve sent a 6-digit reset code.
            </p>
            <p className="text-gray-500 text-sm mb-8">
              Didn&apos;t receive an email? Check your spam folder or try again.
            </p>
            <Link to={`/reset-password?email=${encodeURIComponent(email)}`}>
              <Button className="w-full bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-semibold mb-3">
                Enter Reset Code
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="w-full border-gray-600 text-gray-300 hover:bg-white/5">
                Back to Login
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-white/5 border-gray-800 backdrop-blur">
        <Link to="/login" className="inline-flex items-center text-gray-400 hover:text-white mb-6 transition">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Login
        </Link>

        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-[#F2D857]/20 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-[#F2D857]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Forgot Password?</h2>
          <p className="text-gray-400">
            No worries! Enter your email and we&apos;ll send you reset instructions.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 text-red-400">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div>
            <Label htmlFor="email" className="text-gray-300">Email Address</Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-11 bg-white/5 border-gray-700 text-white placeholder:text-gray-500"
                required
              />
            </div>
          </div>

          <Button 
            type="submit" 
            className="w-full bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-semibold h-11"
            disabled={isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-gray-500 text-sm">
            Remember your password?{' '}
            <Link to="/login" className="text-[#F2D857] hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default ForgotPassword;
